import { useState } from 'react'
import type { ReactElement } from 'react'

import { useSession } from '../../auth/useSession'
import {
  Button,
  CodeLabel,
  ErrorState,
  InputText,
  ItemPicker,
  ProductGroupPicker,
  Spinner,
} from '../../components'
import type { PickedItem } from '../../components'
import { FreshnessBadge } from '../../layout/FreshnessBadge'
import { trpcMessage } from '../../lib/trpcMessages'
import { trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import { AdminPageHeader, ReadOnlyNote, adminStyles as styles, changedFields, hasFields, parseNonNegative } from '../admin/AdminPage'
import { useConfigurationRefresh } from './useConfigurationRefresh'

type SettingsView = RouterOutputs['configuration']['getPlannerSettings']

/** The API's cap on a channel id (`configuration.trpc.ts`). */
const CHANNEL_MAX_LENGTH = 60

/**
 * The planner's parameters that used to be constants in code or a CI variable.
 * Read by the planner and the Slack notice from their next use on.
 */
export const PlannerSettingsPage = (): ReactElement => {
  const settingsQuery = trpc.configuration.getPlannerSettings.useQuery()

  let body: ReactElement
  if (settingsQuery.isPending) {
    body = <Spinner label="Cargando parámetros…" />
  } else if (settingsQuery.isError) {
    body = <ErrorState error={settingsQuery.error} onRetry={() => void settingsQuery.refetch()} />
  } else {
    body = <SettingsForm view={settingsQuery.data} />
  }

  return (
    <section className={styles['admin-page']}>
      <AdminPageHeader
        title="Parámetros del planificador"
        intro="Valores que el planificador y el aviso de Slack leen cada vez que se usan."
        actions={settingsQuery.isSuccess ? <FreshnessBadge freshness={settingsQuery.data.freshness} /> : undefined}
      />
      {body}
    </section>
  )
}

type SettingsFormProps = {
  view: SettingsView
}

/**
 * One form, one save, and only what changed travels: the API refuses an empty
 * patch. Mounted once the settings are loaded, so the draft is seeded by
 * `useState` rather than an effect syncing a form to a query.
 */
const SettingsForm = ({ view }: SettingsFormProps): ReactElement => {
  const { isReadOnly } = useSession()
  const utils = trpc.useUtils()
  const refreshStatus = useConfigurationRefresh()
  const { settings } = view

  const [groupIds, setGroupIds] = useState<Array<number>>(settings.preproductionGroupIds)
  const [shortageGroupIds, setShortageGroupIds] = useState<Array<number>>(settings.shortageGroupIds)
  const [rice, setRice] = useState<Array<PickedItem>>(view.consolidatedRice)
  const [cpCapacity, setCpCapacity] = useState(String(settings.cpPotCapacityGrams))
  const [channels, setChannels] = useState<Array<string>>(settings.slackChannelIds)
  const [newChannel, setNewChannel] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const update = trpc.configuration.updatePlannerSettings.useMutation({
    onSuccess: () => {
      setSaved(true)
      void utils.configuration.getPlannerSettings.invalidate()
      refreshStatus()
    },
    onError: (error) => setErrorMessage(trpcMessage(error)),
  })

  const cpParsed = parseNonNegative(cpCapacity, { integer: true })
  const cpValue = cpParsed.valid ? cpParsed.value : null
  const cpValid = cpValue !== null && cpValue > 0
  const patch = changedFields({
    preproductionGroupIds: settings.preproductionGroupIds,
    shortageGroupIds: settings.shortageGroupIds,
    consolidatedRiceCodes: settings.consolidatedRiceCodes,
    cpPotCapacityGrams: settings.cpPotCapacityGrams,
    slackChannelIds: settings.slackChannelIds,
  }, {
    preproductionGroupIds: groupIds,
    shortageGroupIds,
    consolidatedRiceCodes: rice.map((item) => item.code),
    cpPotCapacityGrams: cpValid && cpValue !== null ? cpValue : settings.cpPotCapacityGrams,
    slackChannelIds: channels,
  })
  const canSave = !isReadOnly && cpValid && hasFields(patch) && !update.isPending

  const edited = (): void => {
    setSaved(false)
    setErrorMessage(null)
  }

  const addChannel = (): void => {
    const channel = newChannel.trim()
    if (channel !== '' && !channels.includes(channel)) {
      setChannels([...channels, channel])
      edited()
    }
    setNewChannel('')
  }

  return (
    <form
      className={styles['admin-page__form']}
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        if (canSave) {
          setErrorMessage(null)
          update.mutate(patch)
        }
      }}
    >
      {isReadOnly && <ReadOnlyNote />}

      <fieldset className={styles['admin-page__fieldset']}>
        <legend className={styles['admin-page__legend']}>Preproducción</legend>
        <ProductGroupPicker
          label="Grupos de producto que son preproducción"
          selected={groupIds}
          disabled={isReadOnly}
          onChange={(ids) => {
            setGroupIds(ids)
            edited()
          }}
        />
      </fieldset>

      <fieldset className={styles['admin-page__fieldset']}>
        <legend className={styles['admin-page__legend']}>Faltas</legend>
        <ProductGroupPicker
          label="Grupos de semi que admiten falta"
          selected={shortageGroupIds}
          disabled={isReadOnly}
          onChange={(ids) => {
            setShortageGroupIds(ids)
            edited()
          }}
        />
      </fieldset>

      <fieldset className={styles['admin-page__fieldset']}>
        <legend className={styles['admin-page__legend']}>Arroces consolidados</legend>
        {rice.length === 0
          ? <p className={styles['admin-page__muted']}>Ningún arroz consolidado.</p>
          : (
            <ul className={styles['admin-page__chips']}>
              {rice.map((item) => (
                <li key={item.code} className={styles['admin-page__chip']}>
                  <CodeLabel code={item.code} title={item.title} known={item.known} />
                  <Button
                    variant="secondary"
                    disabled={isReadOnly}
                    onClick={() => {
                      setRice(rice.filter((held) => held.code !== item.code))
                      edited()
                    }}
                    aria-label={`Quitar ${item.code}`}
                    label="Quitar"
                  />
                </li>
              ))}
            </ul>
          )}
        <ItemPicker
          label="Añadir arroz"
          selected={null}
          disabled={isReadOnly}
          onSelect={(item) => {
            if (item !== null && !rice.some((held) => held.code === item.code)) {
              setRice([...rice, item])
              edited()
            }
          }}
        />
      </fieldset>

      <fieldset className={styles['admin-page__fieldset']}>
        <legend className={styles['admin-page__legend']}>Olla CP</legend>
        <InputText
          label="Capacidad de la olla CP (g)"
          inputMode="numeric"
          value={cpCapacity}
          disabled={isReadOnly}
          hint="Gramos enteros, más de 0."
          onChange={(event) => {
            setCpCapacity(event.target.value)
            edited()
          }}
        />
        {!cpValid && <p role="alert" className={styles['admin-page__error']}>La capacidad tiene que ser un número entero de gramos mayor que 0.</p>}
      </fieldset>

      <fieldset className={styles['admin-page__fieldset']}>
        <legend className={styles['admin-page__legend']}>Canales de Slack</legend>
        <p className={styles['admin-page__note']}>
          Donde se avisa al publicar un plan. Sin canales no se envía el aviso de publicación.
        </p>
        {channels.length > 0 && (
          <ul className={styles['admin-page__chips']}>
            {channels.map((channel) => (
              <li key={channel} className={styles['admin-page__chip']}>
                <span className={styles['admin-page__number']}>{channel}</span>
                <Button
                  variant="secondary"
                  disabled={isReadOnly}
                  onClick={() => {
                    setChannels(channels.filter((held) => held !== channel))
                    edited()
                  }}
                  aria-label={`Quitar el canal ${channel}`}
                  label="Quitar"
                />
              </li>
            ))}
          </ul>
        )}
        <div className={styles['admin-page__inline-form']}>
          <InputText
            label="Id del canal"
            value={newChannel}
            maxLength={CHANNEL_MAX_LENGTH}
            disabled={isReadOnly}
            hint="El id de Slack (C0123ABCD), no el nombre: está en los detalles del canal."
            onChange={(event) => setNewChannel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                addChannel()
              }
            }}
          />
          <Button variant="secondary" disabled={isReadOnly || newChannel.trim() === ''} onClick={addChannel} label="Añadir canal" />
        </div>
      </fieldset>

      {errorMessage !== null && <p role="alert" className={styles['admin-page__error']}>{errorMessage}</p>}
      {saved && !hasFields(patch) && <p role="status" className={styles['admin-page__ok']}>Parámetros guardados.</p>}
      <div className={styles['admin-page__form-actions']}>
        <Button type="submit" disabled={!canSave} label={update.isPending ? 'Guardando…' : 'Guardar parámetros'} />
      </div>
    </form>
  )
}
